import { Check, MoveLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthShell } from '@/components/layout/AuthShell';
import { YellowUnderline } from '@/components/patterns/YellowUnderline';
import { toast } from '@/lib/notify';

import { useCompleteOnboarding } from '../api/useCompleteOnboarding';
import { MarketingCarousel } from '../components/MarketingCarousel';
import { OnboardingStepper } from '../components/OnboardingStepper';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../store/onboardingStore';
import { postAuthLanding } from '../utils/postAuthLanding';

// /onboarding/welcome — Step 3 of the 3-screen onboarding flow.
//
// Final step. "Finish Up" fires the single atomic POST
// /api/v1/me/complete-onboarding that persists personal details, creates
// the workspace, and stamps OnboardingCompletedAt in one transaction.
// On success, routes to the post-auth landing page.
//
// If the guard detects missing required fields (firstName/lastName/
// workspaceName) from step 1, it redirects back to /onboarding/your-details
// before making any API call.
export function WorkflowSetupScreen() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const yourDetailsDraft = useOnboardingStore((s) => s.yourDetails);
  const workspaceInfoDraft = useOnboardingStore((s) => s.workspaceInfo);
  const completeOnboarding = useCompleteOnboarding();

  const getMissingDetailsTarget = () => {
    const hasRequiredDetails = Boolean(
      yourDetailsDraft.firstName?.trim() &&
        yourDetailsDraft.lastName?.trim() &&
        yourDetailsDraft.workspaceName?.trim(),
    );

    return hasRequiredDetails ? null : '/onboarding/your-details';
  };

  const advance = async () => {
    const missingTarget = getMissingDetailsTarget();
    if (missingTarget) {
      navigate(missingTarget, { replace: true });
      return;
    }

    if (user && !user.isSuperAdmin && !user.onboardingCompleted) {
      const workspaceName = yourDetailsDraft.workspaceName ?? '';
      try {
        await completeOnboarding.mutateAsync({
          firstName: yourDetailsDraft.firstName ?? '',
          lastName: yourDetailsDraft.lastName ?? '',
          workspaceName,
          industry: workspaceInfoDraft.industry || undefined,
          teamSize: workspaceInfoDraft.teamSize || undefined,
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not finish onboarding.');
        return;
      }
    }

    // Re-read user after mutation so markOnboardingComplete() has fired and
    // postAuthLanding sees the updated flag.
    const updated = useAuthStore.getState().user;
    const target = updated ? postAuthLanding(updated) : '/login';
    navigate(target, { replace: true });
  };

  const submitting = completeOnboarding.isPending;

  return (
    <AuthShell
      marketing={<MarketingCarousel />}
      footer={
        <div className="w-full flex items-center justify-between flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/onboarding/workspace-info')}
            className="btn btn-primary-light rounded-full!"
            disabled={submitting}
          >
            <MoveLeft className="w-6 h-6 shrink-0" />
            <span>Back</span>
          </button>
          <button
            type="button"
            onClick={advance}
            className="btn btn-primary rounded-full!"
            disabled={submitting}
          >
            <span>{submitting ? 'Finishing...' : 'Finish Up'}</span>
            <Check className="w-6 h-6 shrink-0" />
          </button>
        </div>
      }
    >
      <div className="max-w-[419px] w-full shrink-0 flex flex-col gap-4 sm:gap-6 lg:gap-8">
        <OnboardingStepper step={3} />
        <div className="flex flex-col gap-1 sm:gap-2 lg:gap-2.5 text-center mt-5">
          <h1 className="text-xl lg:text-2xl lg:leading-[29px] font-bold text-black-light">
            New to <YellowUnderline>workflows?</YellowUnderline>
          </h1>
          <p className="text-base lg:text-lg leading-[150%] font-medium">
            Watch a quick demo with this 2-minute guide
          </p>
        </div>
        <div className="flex flex-col gap-5.5">
          <div className="rounded-[30px] overflow-hidden w-full max-h-[300px] aspect-video">
            <video controls autoPlay muted className="w-full h-full aspect-video">
              <source src="/video.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </AuthShell>
  );
}
